import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { Product } from "../../../interfaces/DTO/mislaka_xml_models";
import { colorContributionType, colorProductType } from "../../../utils/colorEnumFormatter";
import {
  formatAdjustedProductType,
  formatContributionType,
  formatDepositorType,
  formatDepositTypeCode,
  formatProductType
} from "../../../utils/enumFormatter";
import { formatCurrency, formatIsoDate } from "../../../utils/namesFormater";
import { MimshakGroupByService } from "../../../services/MimshakGroupByService";
import { MimshakService } from "../../../services/mimshakService";
import {
  formatPeriodLabel,
  colorByAlpha,
  stackTotalTooltipFooter,
  ChartContainer,
  finalValueLabelPlugin,
  buildChartOptions,
  buildBarDataset,
  buildLineDataset,
  buildAreaDataset,
  BaseDataset
} from "../../../shared/UIParts/chartUtils";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

interface Props {
  products: Product[];
}

const DepositsChartWithToggle: React.FC<Props> = ({ products }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<number | null>(null);
  const [mode, setMode] = useState<"monthly" | "cumulative">("cumulative");
  const [groupBy, setGroupBy] = useState<"contribution_type" | "product_type">("product_type");

  const allPolicies = useMemo(() => products.flatMap(p => p.policies_or_accounts), [products]);
  const latestUpdateDate = useMemo(() => MimshakService.getLatestUpdateDate(allPolicies), [allPolicies]);

  const allDepositsEnriched = useMemo(() =>
    latestUpdateDate
      ? MimshakService.getDepositsForLast12Months(allPolicies, latestUpdateDate).map(d => {
          const product = products.find(p => p.policies_or_accounts.some(pol => pol.deposits?.includes(d)));
          return { ...d, adjusted_product_type: product?.details?.adjusted_product_type };
        })
      : [],
    [allPolicies, latestUpdateDate, products]
  );

  const aggregated: Record<number, Record<string, number>> = useMemo(() => {
    if (!latestUpdateDate) return {};
    return groupBy === "contribution_type"
      ? MimshakGroupByService.groupByDepositsByPeriodAndCategoryTypeFillingEmptyLast12(allPolicies, latestUpdateDate)
      : MimshakGroupByService.groupByDepositsByPeriodAndProductTypeFillingEmptyLast12(products, latestUpdateDate);
  }, [allPolicies, latestUpdateDate, groupBy, products]);

  const uniquePeriods = useMemo(() => Object.keys(aggregated).map(Number).sort((a, b) => a - b), [aggregated]);

  const hasDeposits = useMemo(() =>
    Object.values(aggregated).some(period =>
      Object.values(period).some(amount => amount > 0)
    ), [aggregated]);

  const allTypes = useMemo(() => Array.from(new Set(uniquePeriods.flatMap(period => Object.keys(aggregated[period] || {})))), [aggregated, uniquePeriods]);

  useEffect(() => {
    if (uniquePeriods.length > 0) {
      const last = [...uniquePeriods].reverse().find(period =>
        Object.values(aggregated[period] || {}).some(amount => amount > 0)
      );
      setSelectedPeriod(last ?? uniquePeriods[uniquePeriods.length - 1]);
    }
  }, [uniquePeriods, aggregated]);

  const chartData = useMemo(() => {
    const data = uniquePeriods.map((period, index) => {
      const current: Record<string, number | string> = { period: period.toString() };
      allTypes.forEach(type => {
        current[type] = mode === "monthly"
          ? aggregated[period]?.[type] || 0
          : uniquePeriods.slice(Math.max(0, index - 11), index + 1).reduce((sum, p) => sum + (aggregated[p]?.[type] || 0), 0);
      });
      return current;
    });

    return {
      labels: data.map(d => formatPeriodLabel(d.period)),
      datasets: allTypes.map(type => {
        const values = data.map(d => d[type] as number);
        const isLine = mode === "cumulative";
        const baseColor = groupBy === "product_type" ? colorProductType(Number(type)) : colorContributionType(Number(type));
        const color = groupBy === "product_type" ? colorByAlpha(baseColor, Number(type)) : baseColor;

        const baseDataset: BaseDataset = {
          label: groupBy === "product_type" ? formatProductType(Number(type)) : formatContributionType(Number(type)),
          data: values,
          backgroundColor: color,
          borderColor: color,
          finalValueLabel: isLine ? formatCurrency(values[values.length - 1]) : undefined
        };

        if (groupBy === "product_type" && mode === "cumulative") {
          return buildAreaDataset(baseDataset);
        }

        return isLine ? buildLineDataset(baseDataset) : buildBarDataset(baseDataset);
      })
    };
  }, [aggregated, uniquePeriods, allTypes, mode, groupBy]);

  const chartOptions = useMemo(() =>
    buildChartOptions({
      isLineChart: mode === "cumulative",
      isStacked: mode !== "cumulative" || (groupBy === "product_type" && mode === "cumulative"),
      showFooterTotal: true,
      onDatasetClick: (_dataset, _dataIndex) => {
        setSelectedPeriod(Number(uniquePeriods[_dataIndex]));
      },
      formatValue: formatCurrency
    }),
  [mode, uniquePeriods, groupBy]);

  if (!hasDeposits) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          התפלגות הפקדות לפי סוג תרומה (לפי תקופת תשלום)
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small">
            <InputLabel>קיבוץ לפי</InputLabel>
            <Select value={groupBy} label="קיבוץ לפי" onChange={(e: SelectChangeEvent) => setGroupBy(e.target.value as any)}>
              <MenuItem value="contribution_type">סוג תרומה</MenuItem>
              <MenuItem value="product_type">סוג מוצר</MenuItem>
            </Select>
          </FormControl>
          <Divider orientation="vertical" flexItem />
          <Button variant={mode === "monthly" ? "contained" : "outlined"} onClick={() => setMode("monthly")}>הפקדות חודשיות →</Button>
          <Divider orientation="vertical" flexItem />
          <Button variant={mode === "cumulative" ? "contained" : "outlined"} onClick={() => setMode("cumulative")}>הפקדות מצטברות →</Button>
        </Stack>
      </Stack>

      <ChartContainer height={300}>
        <Chart
          type={mode === "cumulative" ? "line" : "bar"}
          data={chartData}
          options={chartOptions}
          plugins={mode === "cumulative" ? [finalValueLabelPlugin] : []}
        />
      </ChartContainer>

      {selectedPeriod && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            פרטי הפקדות עבור תקופת תשלום {selectedPeriod}
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>תאריך הפקדה</TableCell>
                  <TableCell align="right">מוצר</TableCell>
                  <TableCell>סוג הפקדה</TableCell>
                  <TableCell>סוג תרומה</TableCell>
                  <TableCell>סוג מפקיד</TableCell>
                  <TableCell>סכום הפקדה</TableCell>
                  <TableCell>בסיס שכר</TableCell>
                  <TableCell>חודש שכר</TableCell>
                  <TableCell>תקופת תשלום</TableCell>
                  <TableCell>שם המעסיק</TableCell> {/* חדש */}
                  <TableCell>אחוז הפרשה</TableCell> {/* חדש */}
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const filtered = allDepositsEnriched
                    .filter(d => d.payment_period === selectedPeriod);

                  const effectiveSum: Record<string, Record<number, number>> = {};
                  const reportedSum: Record<string, Record<number, number>> = {};
                  filtered.forEach(d => {
                    const key = `${d.employer_name || ""}__${d.salary_month || ""}`;
                    const ct = d.contribution_type as number;
                    const rate = d.deposit_rate;
                    const paid = d.deposit_amount_paid;
                    const absPaid = Math.abs(paid);
                    const reported = d.salary_base || 0;
                    if (!effectiveSum[key]) effectiveSum[key] = {};
                    if (!reportedSum[key]) reportedSum[key] = {};
                    let derived: number;
                    if (rate != null && rate > 0 && absPaid > 0) {
                      derived = absPaid / (rate / 100);
                    } else {
                      derived = reported;
                    }
                    effectiveSum[key][ct] = (effectiveSum[key][ct] || 0) + (paid >= 0 ? derived : -derived);
                    reportedSum[key][ct] = (reportedSum[key][ct] || 0) + reported;
                  });
                  const mergedEffectiveSalaryBase: Record<string, Record<number, number>> = {};
                  Object.entries(effectiveSum).forEach(([key, byType]) => {
                    Object.entries(byType).forEach(([ct, eff]) => {
                      const rep = (reportedSum[key] || {})[Number(ct)] || 0;
                      if (rep > 0 && Math.abs(eff - rep) / rep > 0.01 || rep === 0 && eff !== 0) {
                        if (!mergedEffectiveSalaryBase[key]) mergedEffectiveSalaryBase[key] = {};
                        mergedEffectiveSalaryBase[key][Number(ct)] = eff;
                      }
                    });
                  });

                  const shownGroups = new Set<string>();
                  return filtered.map((deposit, index) => {
                    const key = `${deposit.employer_name || ""}__${deposit.salary_month || ""}`;
                    const isFirstInGroup = !shownGroups.has(key);
                    if (isFirstInGroup) shownGroups.add(key);
                    const derivedByType = mergedEffectiveSalaryBase[key] || {};


                    const reported = deposit.deposit_rate != null ? deposit.deposit_rate : null;
                    const absPaid = Math.abs(deposit.deposit_amount_paid);
                    const reportedBase = deposit.salary_base || 0;
                    const calculatedRate = reportedBase > 0 ? Math.round((absPaid / reportedBase) * 1000) / 10 : null;
                    const reportedRateRounded = reported != null ? Math.round(reported * 10) / 10 : null;
                    const isDifferent = calculatedRate != null && (reported == null || reportedRateRounded !== calculatedRate);

                    return (
                      <React.Fragment key={index}>
                        <TableRow>
                          <TableCell>{formatIsoDate(deposit.deposit_date)}</TableCell>
                          <TableCell align="right">{deposit.adjusted_product_type != null ? formatAdjustedProductType(deposit.adjusted_product_type) : "-"}</TableCell>
                          <TableCell>{formatDepositTypeCode(deposit.deposit_type_code)}</TableCell>
                          <TableCell>{formatContributionType(deposit.contribution_type)}</TableCell>
                          <TableCell>{formatDepositorType(deposit.depositor_type)}</TableCell>
                          <TableCell>{formatCurrency(deposit.deposit_amount_paid)}</TableCell>
                          <TableCell>
                            <>
                              {formatCurrency(deposit.salary_base)}
                              {isDifferent && reported != null && reported > 0 && (
                                <Typography component="span" sx={{ color: "blue", display: "block", fontSize: "0.75rem" }}>
                                  מחושב: {formatCurrency(deposit.deposit_amount_paid / (reported / 100))}
                                </Typography>
                              )}
                              {derivedByType[deposit.contribution_type as number] != null && (
                                <Typography component="span" sx={{ color: "blue", display: "block", fontSize: "0.75rem", mt: 0.5 }}>
                                  סה"כ מחושב: {formatCurrency(derivedByType[deposit.contribution_type as number])}
                                </Typography>
                              )}
                            </>
                          </TableCell>
                          <TableCell>{deposit.salary_month}</TableCell>
                          <TableCell>{deposit.payment_period}</TableCell>
                          <TableCell>{deposit.employer_name || "-"}</TableCell>
                          <TableCell>
                            <>
                              {reported != null ? `${reported.toFixed(2)}%` : "-"}
                              {isDifferent && calculatedRate != null && (
                                <Typography component="span" sx={{ color: calculatedRate < 0 ? "red" : "blue", display: "block", fontSize: "0.75rem" }}>
                                  בפועל: <span dir="ltr">{calculatedRate.toFixed(1)}%</span>
                                </Typography>
                              )}
                            </>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  });
                })()}
                <TableRow sx={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}>
                  <TableCell colSpan={4}>סה"כ הפקדות</TableCell>
                  <TableCell>
                    {formatCurrency(
                      allDepositsEnriched
                        .filter(d => d.payment_period === selectedPeriod)
                        .reduce((sum, deposit) => sum + deposit.deposit_amount_paid, 0)
                    )}
                  </TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default DepositsChartWithToggle;
